/**
 * board 2106 M4 — THE WIRE VOCABULARY OF `POST /api/bookings/:id/balance-ask`.
 *
 * ⛔ A CLOSED SET OF CODES, NEVER A SENTENCE, on `SEND_ERROR_CODES`' rule and for
 * its reason: the client switches its operator copy on the CODE, and matching on
 * prose is how a reworded refusal silently changes what Helen is told. The
 * sentence still travels in `error`; the code is what is keyed on.
 *
 * ⛔ IT IS A SEPARATE LIST FROM `SEND_ERROR_CODES`, deliberately. That constant is
 * pinned byte-for-byte by test/shared/send-schema.spec.ts and its companion
 * `SEND_ERROR_COPY` is a `Record<SendErrorCode, …>` that `tsc` checks for
 * exhaustiveness — so adding a balance code there would force somebody to write
 * SEND-route copy for a refusal that route cannot give. `server/app.ts`'s `err`
 * takes a union of these registries for exactly this reason.
 *
 * ⚠️ THE OVERLAP WITH `SEND_ERROR_CODES` IS REAL AND IS NOT AN ACCIDENT:
 * 'sms_unreachable_country', 'sms_unreadable_number', 'sms_unconfigured',
 * 'stripe_unconfigured' and 'account_booking' are the SAME conditions with the
 * SAME operator remedies as on the send route, and they keep the same spellings
 * so one condition reads as one thing across both surfaces. They are re-declared
 * rather than imported because the two lists are free to diverge — a balance ask
 * can refuse things a quote send cannot, and the reverse.
 */

export const BALANCE_ASK_ERROR_CODES = [
  // ── The request itself ────────────────────────────────────────────────────
  /** The body named a channel this route cannot drive. Only 'email' and 'text'
   *  exist; anything else is a client defect, never a silent fallback to one of
   *  them (texting a customer who was told they would be emailed is dishonest). */
  'unsupported_channel',

  // ── Already done ──────────────────────────────────────────────────────────
  /**
   * ⛔ RENAMED FROM THE ISSUER'S `already_invoiced`, and the rename is the honest
   * one: on the TEXT channel no invoice exists at all — the customer was sent a
   * Payment Link — so "already invoiced" would describe a Stripe object that was
   * never created. What is true on both channels is that this booking has already
   * been ASKED.
   */
  'already_asked',

  // ── No way to reach this customer ─────────────────────────────────────────
  /** No email on file: the email rail cannot reach them. `no_phone`'s sibling,
   *  spelled as the send route spells it. */
  'no_email',
  /** No mobile number on file: the text rail cannot reach them. */
  'no_phone',
  /** The sender cannot reach this number's country (board 2089's table). The
   *  remedy is the other channel, which is why it is not folded into `no_phone`:
   *  the booking HAS a number and it is correct. */
  'sms_unreachable_country',
  /** The stored number cannot be placed in any country, so there is nothing to
   *  grade. The OPPOSITE remedy to the line above: correct the number. */
  'sms_unreadable_number',

  // ── This deployment cannot do it ──────────────────────────────────────────
  /** No Stripe client, or one without the methods this ask needs. 503. */
  'stripe_unconfigured',
  /** No customer-SMS transport is configured on this deployment. 503. */
  'sms_unconfigured',
  /**
   * board 2106 M4 (security rework) — THIS DEPLOYMENT'S PUBLIC SITE URL IS NOT
   * FIT TO RECEIVE A PAYER, so no text ask may mint a link that redirects to it.
   *
   * ⛔ A 503 LIKE ITS TWO NEIGHBOURS, NOT A 409: nothing about the BOOKING is
   * wrong and no operator action on it helps. The redirect is baked into a
   * customer-payable Stripe object at mint time, so an `http://` or loopback
   * value is a deployment fault that has to be fixed before anyone can be asked.
   */
  'site_url_unconfigured',
  /** The killswitch is off (`ECMB_BALANCE_INVOICE` is not "1"). A POSTURE, not a
   *  fault: the deposit is untouched and the balance is collected by hand. */
  'balance_invoicing_disabled',

  // ── The booking is not in a state that owes a consumer balance ────────────
  /** No paid deposit, so there is no balance stage to enter. */
  'no_paid_payment',
  /** More than one paid deposit: which one a balance completes decides the
   *  amount, so the issuer refuses to guess and so does this. */
  'ambiguous_payment',
  /** The deposit names no quote, or a quote with no figure. */
  'no_quote',
  /** The deposit on record is not this quote's deposit (a re-price). */
  'deposit_total_mismatch',
  /** total - deposit <= 0: nothing is owed. */
  'nothing_to_invoice',
  /** Billed to an account: the monthly invoice rail, no deposit and no balance.
   *  PERMANENT for this booking, which is why it has its own code. */
  'account_booking',
  /** ⭐ board 2814 M4 - Cash, paid to the driver on the day: nothing is asked for
   *  online. PERMANENT for this run unless its payer is changed on the booking. */
  'cash_to_driver',
  /** ⭐ board 2814 M7 - passed to a taxi firm: nothing is asked for. Lifted only
   *  by undoing the pass on the booking. */
  'taxi_passed',
  /** Not a confirmed booking, so there is no journey to ask a balance for. */
  'booking_not_confirmed',
  /**
   * ⛔⛔ board 2106 M5 rework — THE DEPOSIT PREDATES THE BALANCE RAIL (or when it
   * was paid is not recorded), so this balance may already have been taken by
   * hand and asking again would be a DOUBLE BILL of a real customer.
   *
   * ⛔ THE SPELLING IS `core/balance-reasons.ts`' EXISTING MEMBER, not a new one.
   * `bin/balance-report.ts` has labelled these bookings `paid_before_rail` since
   * M2 (plan decision D8); one condition must read as one thing across the
   * report, the CLI's refusal and this route, which is the house rule the
   * `no_email` / `no_phone` renames followed.
   *
   * ⛔ 409, AND THERE IS NO ON-SCREEN OVERRIDE. The documented way past it is
   * `balance:issue --confirmed-not-collected`, which is a human having asked
   * Helen; whether the kiosk ever gets a button for it is Alex's ruling
   * (docs/OPEN-QUESTIONS.md Q150 item 2).
   */
  'paid_before_rail',

  // ── Faults: something WAS attempted and did not complete (502) ────────────
  /**
   * ⛔ THE AUDIT ROW COULD NOT BE WRITTEN, SO NOTHING WAS ASKED (D14). It is a
   * FAULT rather than a refusal because the request was well-formed and the
   * booking was askable; what failed was our own record of who asked. Fail-closed
   * is the whole design: an ask nobody is named for must not happen.
   */
  'audit_failed',
  /** A Stripe call failed, so no payable link exists. Any row this ask opened has
   *  been closed behind it. */
  'stripe_failed',
  /**
   * The link was minted and the TEXT did not go out. Either the transport was
   * never reached (the send ledger row could not be written) or the link has
   * been switched off; the row is voided either way, so nothing payable reached
   * the customer.
   * ⛔ Its message is FIXED and never the provider's: a `TwilioApiError` message
   * embeds Twilio's `detail`, which echoes the To number.
   */
  'text_not_sent',
  /**
   * ⛔ board 2811 M8 (M7 security review, carried): the transport FAILED and the
   * link could NOT be switched off. A transport failure can be ambiguous, so the
   * text may have reached the customer with a payable link; the row is left a
   * `draft` with that link (it reads `failed`/`unfinished`). NOT "nothing was
   * asked": the screen sends Helen to Alex before any second ask or phone payment.
   * Its message is fixed, for `text_not_sent`'s reason.
   */
  'text_not_sent_link_live',
] as const;

export type BalanceAskErrorCode = (typeof BALANCE_ASK_ERROR_CODES)[number];

/** Boundary guard: the client reads `code` off an untrusted response body. */
export function isBalanceAskErrorCode(v: unknown): v is BalanceAskErrorCode {
  return typeof v === 'string' && (BALANCE_ASK_ERROR_CODES as readonly string[]).includes(v);
}

/**
 * The states a booking's balance ask can be in, as a screen says them.
 * board 2811 M4 (H8) added `scheduled`: an automatic balance EMAIL Stripe has not
 * sent yet (`isScheduledBalanceEmail`, core/data/balance-invoices-repo.ts), told
 * apart from `asked` because the screen dates it by when it WILL go (`sendsAt`).
 * board 2811 M7 added three TEXT states (plan M7 step 1, A2 item 6, A3 item 1):
 *   - `due`: a scheduled text whose instant has come, not sent yet, still inside
 *     `BALANCE_TEXT_LATE_AFTER_MS` (core/balance-ask-timing.ts);
 *   - `late`: the same, past that window;
 *   - `failed`: the text did not reach the customer, or a link is attached and
 *     its send never finished; `BalanceTextFailure` below says which.
 *   A text `scheduled` row is a draft with no link and a future `finalize_at`.
 *   `stuck` is now an EMAIL-only state.
 *
 * ⛔ `closed` AND `none` ARE DIFFERENT FACTS AND THE WHOLE CONTROL TURNS ON IT:
 * "nobody has been asked" and "somebody was asked and it was retracted" call for
 * opposite operator actions.
 *
 * ⛔ DECLARED HERE, IN THE ONE MODULE BOTH HALVES ALREADY IMPORT (board 2106 M5
 * rework, R12.3). M5 wrote the list twice — once in
 * `core/data/balance-invoices-repo.ts` and once inline on
 * `ApiBookingDetail.balance` — with nothing pinning the two equal, so a sixth
 * state, or a rename, would have compiled on both sides while they disagreed
 * about what the wire carries. `BalanceAskChannel` below is here for exactly this
 * reason; this joins it.
 *
 * ⛔ THE ARRAY'S READER IS NAMED, BECAUSE UNTIL THE M5 REWORK 3 IT HAD NONE —
 * only the `type` on the next line read it, so the list was inert and a sixth
 * member would have shipped graded by nothing. `test/core/balance-reasons.spec.ts`
 * ("every state BALANCE_ASK_STATES declares is one this reader actually
 * produces") now iterates it against `getBalanceAskSummary`, the only code that
 * can produce a state, with a `Record<BalanceAskState, …>` so a new member fails
 * `tsc` until its shape is written. Re-derive the readers:
 *   `/usr/bin/grep -rn BALANCE_ASK_STATES src test`
 */
export const BALANCE_ASK_STATES = ['none', 'scheduled', 'due', 'late', 'asked', 'stuck', 'failed', 'paid', 'closed'] as const;
export type BalanceAskState = (typeof BALANCE_ASK_STATES)[number];

/**
 * board 2811 M7: WHY A TEXT ASK READS `failed`, because the three call for three
 * different things from Helen (plan A3 item 1 and its cycle-5 note):
 *   - `not_sent`: the row is VOID after a compensated failure (the send, the
 *     ledger write or the mint failed, or the carrier failed it before it was
 *     closed). No live link is left, so her one-press ask is offered again.
 *   - `not_delivered`: the carrier reported `failed` or `undelivered` on a row
 *     that still carries its link. The link is live; no control (a re-ask would
 *     refuse `already_asked`): she rings the customer.
 *   - `unfinished`: a link is attached and the send never finished (the ledger
 *     is still `composed` after `STUCK_TEXT_AFTER_MS`, or `send_failed` on a row
 *     that could not be voided). The existing stuck instructions stand.
 * Null on every other state.
 */
export const BALANCE_TEXT_FAILURES = ['not_sent', 'not_delivered', 'unfinished'] as const;
export type BalanceTextFailure = (typeof BALANCE_TEXT_FAILURES)[number];

/** The two channels a balance may be asked for on. Exported so the route, the
 *  CLI and the ask share one definition rather than three string literals. */
export const BALANCE_ASK_CHANNELS = ['email', 'text'] as const;
export type BalanceAskChannel = (typeof BALANCE_ASK_CHANNELS)[number];

export function isBalanceAskChannel(v: unknown): v is BalanceAskChannel {
  return typeof v === 'string' && (BALANCE_ASK_CHANNELS as readonly string[]).includes(v);
}
